<script lang="ts">
import { PropType, defineComponent, h, resolveComponent, type VNode } from 'vue';

import { getFormattedAddress } from '@/bundles/App/helpers/address/getFormattedAddress';
import { formatCurrency, formatDate, formatPercent } from '@/utils/filters';
import { getAccountRouteHandler } from '@/bundles/App/helpers';
import { IComparable, TransactionTypesEnum } from '@/bundles/Comparables/types';
import { getComparableLeaseTermLength } from '@/bundles/Comparables/helpers';

import AvailabilityChipsWrapper from '@/bundles/Availabilities/components/chips/AvailabilityChipsWrapper.vue';
import Label from '@/bundles/Common/components/Label.vue';
import BuildingSizeLabel from '@/bundles/MeasurementUnits/components/BuildingSizeLabel.vue';
import LandSizeLabel from '@/bundles/MeasurementUnits/components/LandSizeLabel.vue';
import POLIcon from '@/bundles/Comparables/components/POLIcon.vue';
import ComparableSalesPriceLabel from '@/bundles/Comparables/components/labels/ComparableSalesPriceLabel.vue';

export default defineComponent({
  name: 'ComparableMobileCard',

  props: {
    item: {
      type: Object as PropType<IComparable>,
      required: true
    },
    activeAccountId: {
      type: Number,
      required: true
    }
  },

  render (): VNode {
    const VCard = resolveComponent('v-card');
    const VImg = resolveComponent('v-img');
    const RouterLink = resolveComponent('router-link');

    const isSale = this.item.transaction_type === TransactionTypesEnum.sale;

    return h(VCard, {
      ...this.$attrs,
      variant: 'outlined',
      class: ['comparable-mobile-card', this.$attrs.class],
    }, { default: () => [
      h('div', { class: 'd-flex gap-2' }, [
        h(VImg, {
          src: this.item.photo_links?.medium_url || this.item?.photo?.url || this.$placeholderPropertyImg,
          aspectRatio: 16 / 9,
          cover: false,
          position: 'center top',
          minWidth: '160px',
          maxWidth: '320px',
          class: 'comparable-mobile-card__img'
        }, { default: () => [
          h('div', { class: 'transaction-type-label' }, isSale ? 'SOLD' : 'LEASED'),
        ] }),
        h('div', { class: 'comparable-mobile-card__address-info px-3 pt-3' }, [
          h('div', { class: 'record-id' }, `Comparable ID# ${this.item._key}`),
          h(RouterLink, {
            to: getAccountRouteHandler({
              name: 'comparable',
              params: { id: this.item._key },
              activeAccountId: this.activeAccountId,
            }),
            class: 'font-weight-bold app-link comparable-mobile-card__name'
          }, { default: () => this.item.name }),
          h('div', { class: 'white-space--pre-line comparable-mobile-card__address' }, [
            getFormattedAddress(this.item.address, { includeStreet2: true })
          ])
        ])
      ]),
      h('div', { class: 'pa-3 pt-2 d-flex flex-column gap-2' }, [
        h(AvailabilityChipsWrapper, {
          type: this.item.property_type,
          mixedUse: this.item.mixed_use,
          buildingClass: this.item.property.building_class,
          inHouse: this.item.in_house,
          isPortfolio: this.item.is_portfolio,
          isOneLine: true,
          allowWrap: true
        }),
        h('div', { class: 'd-flex gap-2 flex-column' }, [
          h('div', { class: 'd-flex gap-3' }, [
            h('div', { class: 'comparable-mobile-card__info' }, [
              h('div', { class: 'comparable-mobile-card__info-label comparable-mobile-card__comp-size-label' }, 'Comp Size:'),
              h('div', { class: 'comparable-mobile-card__comp-size' }, [
                h(BuildingSizeLabel, { value: this.item.transaction.total_square_feet })
              ])
            ]),
            h('div', { class: 'comparable-mobile-card__info' }, [
              h('div', {
                  class: `comparable-mobile-card__info-label ${isSale ? 'comparable-mobile-card__sale-price-label' : 'comparable-mobile-card__lease-rate-starting-label'}`
                },
                isSale ? 'Sale Price (PSF):' : 'Lease Rate Starting (Ann Esc):'
              ),
              h('div', {
                class: isSale ? 'comparable-mobile-card__sale-price' : 'comparable-mobile-card__lease-rate-starting'
              }, [
                isSale
                  ? h(ComparableSalesPriceLabel, {
                      propertyType: this.item.property?.type || '',
                      totalAcres: this.item.transaction.total_acres,
                      totalPrice: this.item.transaction.total_price,
                      totalPricePer: this.item.transaction.total_price_per,
                      hideLabel: true,
                    })
                  : h(Label, {
                      value: this.item.transaction.starting_rent_sf ? `${formatCurrency(this.item.transaction.starting_rent_sf)} / SF (${formatPercent(this.item.transaction.escalation)})` : null
                    })
              ])
            ])
          ]),
          h('div', { class: 'd-flex gap-3' }, [
            h('div', { class: 'comparable-mobile-card__info' }, [
              h('div', { class: 'comparable-mobile-card__info-label comparable-mobile-card__building-size-label' }, 'Building Size:'),
              h('div', { class: 'comparable-mobile-card__building-size' }, [
                h(BuildingSizeLabel, { value: this.item.property.building_size })
              ])
            ]),
            h('div', { class: 'comparable-mobile-card__info' }, [
              h('div', {
                class: `comparable-mobile-card__info-label ${isSale ? 'comparable-mobile-card__cap-rate-label' : 'comparable-mobile-card__lease-rate-effective-label'}`
              }, isSale ? 'Cap Rate:' : 'Lease Rate Effective:'),
              h('div', {
                class: isSale ? 'comparable-mobile-card__cap-rate' : 'comparable-mobile-card__lease-rate-effective'
              }, [
                isSale
                    ? h(Label, { value: this.item.transaction.cap_rate ? formatPercent(this.item.transaction.cap_rate) : null })
                    : h(Label, { value: this.item.transaction.effective_rent_sf ? `${formatCurrency(this.item.transaction.effective_rent_sf)} / SF` : null })
              ])
            ])
          ]),
          h('div', { class: 'd-flex gap-3' }, [
            h('div', { class: 'comparable-mobile-card__info' }, [
              h('div', { class: 'comparable-mobile-card__info-label comparable-mobile-card__land-size-label' }, 'Land Size:'),
              h('div', { class: 'comparable-mobile-card__land-size' }, [
                h(LandSizeLabel, { value: this.item.transaction.total_acres }),
                this.item.transaction.land_pol ? h(POLIcon, { class: 'ml-2' }) : null
              ])
            ]),
            isSale ? null : h('div', { class: 'comparable-mobile-card__info' }, [
              h('div', { class: 'comparable-mobile-card__info-label comparable-mobile-card__lease-comm-term-label' }, 'Lease Comm (term):'),
              h('div', { class: 'comparable-mobile-card__lease-comm-term' }, [
                h(Label, {
                  value: this.item.transaction.lease_commencement ? `${formatDate(this.item.transaction.lease_commencement)} (${getComparableLeaseTermLength(this.item)})` : null
                })
              ])
            ])
          ])
        ])
      ])
    ] })
  }
})
</script>

<style lang="scss" scoped>
.comparable-mobile-card {
  .comparable-mobile-card__img {
    border-top-left-radius: 3px; // outer border radius minus border width
    flex: 1 1 30%;
  }

  .comparable-mobile-card__address-info {
    flex: 1 1 70%;
    line-height: 18px;
  }

  .comparable-mobile-card__info {
    flex: 1 1 50%;
    line-height: 18px;
  }

  .comparable-mobile-card__info-label {
    font-weight: 600;
  }
}
</style>
